"use client";

import React from "react";
import { UtensilsCrossed, GlassWater, Clapperboard, ZapOff, Flower2, Warehouse, ArrowRight } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
const applications = [{
  title: "Restaurants & Commercial Kitchens",
  description: "Temporary refrigerated and freezer storage for restaurants, commercial kitchens, and food-service operations.",
  icon: UtensilsCrossed
}, {
  title: "Catering & Special Events",
  description: "Mobile cold storage for catered events, parties, and special-event food and beverage service.",
  icon: GlassWater
}, {
  title: "Film & Television Productions",
  description: "Mobile refrigeration options for food, beverages, and other approved production needs.",
  icon: Clapperboard
}, {
  title: "Emergency Cold Storage & Outages",
  description: "Temporary cold storage options when additional refrigeration is needed during an outage",
  icon: ZapOff
}, {
  title: "Floral & Perishable Retail",
  description: "Mobile refrigeration for floral products and other temperature-sensitive inventory",
  icon: Flower2
}, {
  title: "Commercial Overflow Storage",
  description: "Potential temporary refrigerated storage for food distribution, grocery operations, facility projects, and commercial overflow needs. Suitability and availability are confirmed individually.",
  icon: Warehouse
}];
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 16
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.16, 1, 0.3, 1]
    }
  }
};
export default function Applications() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="applications-we-serve" data-nav="light" className="bg-background py-14 md:py-20 lg:py-24 border-b border-border">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent text-accent-foreground border border-border text-xs font-semibold uppercase tracking-wider mb-4">
            <span className="size-2 rounded-full bg-primary" />
            Industry Solutions
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-default tracking-tight text-foreground">
            Mobile Refrigeration Rental Applications
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-default">Refrigerated and Freezer trailers deployed directly to your business or event site across Southern California and our listed Las Vegas-area communities.</p>
        </div>

        {/* Applications Grid */}
        <motion.div variants={containerVariants} initial={shouldReduceMotion ? "visible" : "hidden"} whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {applications.map((app, index) => {
          const Icon = app.icon;
          return <motion.div key={app.title} data-index={index} variants={itemVariants} className="h-full">
                <Card className="group h-full bg-card border-border rounded-lg shadow-sm transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-md">
                  <CardContent className="p-6 flex flex-col h-full">
                    <div className="mb-5 flex size-12 items-center justify-center rounded-lg bg-muted text-foreground transition-colors duration-200 group-hover:bg-primary group-hover:text-primary-foreground">
                      <Icon className="size-6 stroke-[1.75]" />
                    </div>
                    <h3 className="text-lg font-semibold font-default text-card-foreground mb-2.5">
                      {app.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed mt-auto font-default">
                      {app.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Action Button */}
        <div className="mt-12 text-center">
          <Button asChild variant="outline" size="lg" className="rounded-md font-semibold text-foreground border-border hover:bg-muted transition-[background-color,border-color]">
            <Link to="/WhoWeServePage">
              See Who We Serve
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}